import React, { FC, ReactNode } from 'react';
import { Link } from 'src/components/widgets/buttons';
import { NotificationBanner } from 'src/components/widgets/notificationBanner';
import { T, backofficeAnonymousUrl } from 'src/shared/helpers';
import styled from 'styled-components';
import { SellerPlanCheckInfo } from './switchTypes';

/**
 * Banner markup for the switch confirmation dialog. Markup only — the buttons and the flow logic stay in
 * switchSalesChannelDialog.tsx, which is what keeps these props lists small.
 *
 * Everything here returns a Fragment, never a wrapping div: the dialog's DialogContent and PlanCheckAlertSection are
 * `flex column gap`, so their children must stay direct flex children or the gap collapses onto one element.
 */

const SALES_CHANNELS_HELP_ANCHOR_URL =
    'https://zakeke.zendesk.com/hc/en-us/articles/4415810409106-Sales-Channels?brand_id=2771725#h_01G5S8E6VBXQJ5BDYFM50NZ8BA';
const PUBLISHED_PRODUCTS_HELP_URL = 'https://zakeke.zendesk.com/hc/en-us/articles/360022582493-What-do-you-mean-by-Published-Products';

export const AlertSectionDivider = styled.hr`
    border: none;
    border-top: 2px solid #e0e0e0;
    margin: 4px 0;
`;

export const PlanCheckAlertSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    border: 1px solid #ca0000;
    border-left: 4px solid #ff0000;
    border-radius: 4px;
`;

/**
 * A single CRITICAL banner boxed in the plan-check frame, followed by a divider. The divider is a sibling of the
 * section in DialogContent's flow, not a child of it.
 */
const PlanCheckAlert: FC<{ title: string; content: ReactNode }> = ({ title, content }) => (
    <>
        <PlanCheckAlertSection>
            <NotificationBanner type='CRITICAL' title={title} content={content} />
        </PlanCheckAlertSection>
        <AlertSectionDivider />
    </>
);

/** The "you must unpublish N products" pair plus its Learn-more link — identical in both product-limit banners. */
const PublishedProductsOverageDetails: FC<{ planCheck: SellerPlanCheckInfo }> = ({ planCheck }) => (
    <>
        {planCheck.maxProductsScale != null && planCheck.currentProductCount - planCheck.maxProductsScale > 0 && (
            <p>
                {T._('To complete the subscription to the Scale plan, you will need to unpublish', 'Admin')}{' '}
                {planCheck.currentProductCount - planCheck.maxProductsScale} {T._('product(s).', 'Admin')}
            </p>
        )}
        {planCheck.maxProductsGrow != null && planCheck.currentProductCount - planCheck.maxProductsGrow > 0 && (
            <p>
                {T._('To complete the subscription to the Grow plan, you will need to unpublish', 'Admin')}{' '}
                {planCheck.currentProductCount - planCheck.maxProductsGrow} {T._('product(s).', 'Admin')}
            </p>
        )}

        <Link href={PUBLISHED_PRODUCTS_HELP_URL} target={'_blank'}>
            {T._('Learn more', 'Admin')}
        </Link>
    </>
);

export const LegacyPlanNotSupportedAlert: FC = () => (
    <NotificationBanner
        type='CRITICAL'
        title={T._('Switch Not Available for Legacy Plans', 'Admin')}
        content={<p>{T._('The sales channel switch cannot be executed on your current plan. Please contact Zakeke support for assistance.', 'Admin')}</p>}
    />
);

const PodProductsAlert: FC = () => (
    <NotificationBanner
        type='CRITICAL'
        title={T._('Print-on-Demand Products Detected', 'Admin')}
        content={
            <>
                <p>
                    {T._(
                        'Your account currently has active Print-on-Demand (POD) products. The sales channel switch cannot be completed while POD products are present, as they are bound to your current Master Integration.',
                        'Admin'
                    )}
                </p>
                <p>
                    {T._(
                        'To proceed, please remove all Print-on-Demand products from your account. Once done, you will be able to continue with the switch.',
                        'Admin'
                    )}
                </p>
                <p>
                    {T._(
                        'After the switch is complete, you will need to set up the POD integration again on the new Master Integration in order to re-import your Print-on-Demand products.',
                        'Admin'
                    )}
                </p>
                <Link href={SALES_CHANNELS_HELP_ANCHOR_URL} target='_blank'>
                    {T._('Learn more', 'Admin')}
                </Link>
            </>
        }
    />
);

const ConfiguratorProductsAlert: FC = () => (
    <NotificationBanner
        type='CRITICAL'
        title={T._('Product Configurator Products Detected', 'Admin')}
        content={
            <>
                <p>
                    {T._(
                        'Your account contains products using 3D Product Configurator, which is not compatible with the sales channel switch. Please remove these products before proceeding.',
                        'Admin'
                    )}
                </p>
                <Link href={SALES_CHANNELS_HELP_ANCHOR_URL} target='_blank'>
                    {T._('Learn more', 'Admin')}
                </Link>
            </>
        }
    />
);

/** Both blockers share one frame, with a divider only when both are present — no trailing divider here. */
export const BlockingProductsAlerts: FC<{ hasPodProducts: boolean; hasConfiguratorProducts: boolean }> = ({ hasPodProducts, hasConfiguratorProducts }) => (
    <PlanCheckAlertSection>
        {hasPodProducts && <PodProductsAlert />}
        {hasPodProducts && hasConfiguratorProducts && <AlertSectionDivider />}
        {hasConfiguratorProducts && <ConfiguratorProductsAlert />}
    </PlanCheckAlertSection>
);

export const ScalePlanRequiredAlert: FC = () => (
    <PlanCheckAlert
        title={T._('Scale Plan Subscription Required Before Switch', 'Admin')}
        content={
            <>
                <p>
                    {T._(
                        'The sales channel switch process includes a subscription step in which you will be asked to select a new Zakeke plan for your new Master Integration.',
                        'Admin'
                    )}
                </p>
                <p>
                    {T._(
                        'Based on your current number of published products, you will need to subscribe to at least the Scale plan to complete the switch.',
                        'Admin'
                    )}
                </p>
                <p>
                    {T._('Before proceeding, please verify that the', 'Admin')}{' '}
                    <Link href={backofficeAnonymousUrl('/pricing')} target='_blank'>
                        {T._('Scale plan', 'Admin')}
                    </Link>{' '}
                    {T._('is suitable for your needs. If you have any doubts, please contact Zakeke support.', 'Admin')}
                </p>
            </>
        }
    />
);

export const ProductLimitReachedAlert: FC<{ planCheck: SellerPlanCheckInfo }> = ({ planCheck }) => (
    <PlanCheckAlert
        title={T._('Product Limit Reached', 'Admin')}
        content={
            <>
                <p>
                    {T._(
                        'The sales channel switch process includes a subscription step in which you will be asked to select a new Zakeke plan for your new Master Integration.',
                        'Admin'
                    )}
                </p>
                <PublishedProductsOverageDetails planCheck={planCheck} />

                <br />
                <br />
                <p>
                    {T._('Please also check our', 'Admin')}{' '}
                    <Link href={backofficeAnonymousUrl('/pricing')} target='_blank'>
                        {T._('pricing page', 'Admin')}
                    </Link>{' '}
                    {T._('before proceeding. If in doubt, please contact Zakeke support.', 'Admin')}
                </p>
            </>
        }
    />
);

/**
 * Unreachable in practice: its call site sits in the branch where isV1PlanBlocked is already false, which makes
 * `!isNotV1ProductLimitReached && planCheck?.isProductLimitReached` a contradiction. Kept verbatim — see
 * docs/sales-channel-switch-refactor.md. Deliberately NOT merged with ProductLimitReachedAlert: the bodies differ,
 * so collapsing them would surface copy that has never rendered.
 */
export const ProductLimitReachedSimpleAlert: FC<{ planCheck: SellerPlanCheckInfo }> = ({ planCheck }) => (
    <PlanCheckAlert
        title={T._('Product Limit Reached', 'Admin')}
        content={
            <>
                <PublishedProductsOverageDetails planCheck={planCheck} />

                <br />
                <br />
                <p>{T._('If in doubt, please contact Zakeke support.', 'Admin')}</p>
            </>
        }
    />
);

export const EstimatedProcessingTimeAlert: FC = () => (
    <NotificationBanner
        type='INFO'
        title={T._('Estimated Processing Time', 'Admin')}
        content={
            <p>{T._('Processing time varies from half a day to a maximum of 1 day, depending on the number and complexity of your products.', 'Admin')}</p>
        }
    />
);

export const CurrencyMismatchAlert: FC<{ secondaryCurrencyCode?: string | null; masterCurrencyCode?: string | null }> = ({
    secondaryCurrencyCode,
    masterCurrencyCode
}) => (
    <NotificationBanner
        type='WARNING'
        title={T._('Currency Difference', 'Admin')}
        content={
            <p>
                {T._(
                    'The sales channel you are switching to uses a different currency ({0}) than your current Master Integration ({1}). If you have set a fixed price for one or more items in your Image and Clipart Gallery, or created pricing rules, please account for the exchange rate difference between the two currencies, as these prices will not be converted automatically.',
                    'Admin'
                )
                    .replace('{0}', secondaryCurrencyCode || '')
                    .replace('{1}', masterCurrencyCode || '')}
            </p>
        }
    />
);

export const ImportantInformationAlert: FC = () => (
    <NotificationBanner
        type='WARNING'
        title={T._('Important Information', 'Admin')}
        content={
            <>
                <p>
                    {T._(
                        'Do you want to delete the current sales channel after the switch? If so, you must publish all products you want to keep after the switch on the sales channel that will become the new Master Integration.',
                        'Admin'
                    )}
                </p>
                <Link href='https://zakeke.zendesk.com/hc/en-us/articles/4415810409106-Sales-Channels#h_01G5S8F7PSYM7H0MHD44K1G6FM' target='_blank'>
                    {T._('Learn more', 'Admin')}
                </Link>
            </>
        }
    />
);

export const SubscriptionRequirementAlert: FC<{ requiresScalePlan: boolean; currentProductCount?: number | null }> = ({
    requiresScalePlan,
    currentProductCount
}) => (
    <NotificationBanner
        type='CRITICAL'
        title={T._('Subscription Requirement', 'Admin')}
        content={
            <>
                <p>{T._('During the subscription step, you will be asked to choose a new Zakeke plan for your new Master Integration.', 'Admin')}</p>
                {requiresScalePlan ? (
                    <p>
                        {T._('You must select at least the SCALE plan to complete the switch, because your current number of published products', 'Admin')}{' '}
                        {currentProductCount != null && <strong>({currentProductCount})</strong>} {T._('exceeds the limit of the GROW plan.', 'Admin')}
                    </p>
                ) : (
                    <p>{T._('You must select at least the GROW plan to complete the switch.', 'Admin')}</p>
                )}
                <p>{T._('Plans below GROW do not support the sales channel switch feature.', 'Admin')}</p>
            </>
        }
    />
);

/** Shown when the two platforms cannot be switched automatically and only a manual migration is possible. */
export const MigrationOnlyAlerts: FC = () => (
    <>
        <NotificationBanner
            type='WARNING'
            title={T._('Migration Only Available', 'Admin')}
            content={
                <p>
                    {T._(
                        'Automatic switching is not supported when the Master Integration and the sales channel are on different platforms (e.g., Shopify vs non-Shopify). In this case, the only option is a manual product migration carried out by our team.',
                        'Admin'
                    )}
                </p>
            }
        />

        <NotificationBanner
            type='INFO'
            title={T._('Ready to get started?', 'Admin')}
            content={
                <p>{T._('Click "Contact Support" to open a conversation with our team. They will assist you with the manual migration process.', 'Admin')}</p>
            }
        />
    </>
);
